import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { appTheme } from 'spa/config/styled'
import { useFormContext,Controller } from 'react-hook-form'
import { QuestionnaireFormLabel } from 'spa/member/components/QuestionnaireFormLabel'
import { NewCheckBox } from 'spa/member/components/NewCheckbox'
import { NewCheckboxProps } from 'spa/member/components/NewCheckbox/types'
import { MultiCheckBoxFormProps } from './types'
import { FormErrorMessage } from 'spa/member/components/FormErrorMessage'

const StyledNewCheckboxContainer = styled.div`
  display: flex;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }

  & > div:not(:last-child) {
    flex: 1;
    margin-right: 12px;
  }

  & > div:last-child {
    flex-basis: 100%;
    margin-top: 16px;
  }
`

const filterNormalValues = (options: MultiCheckBoxFormProps['options']) => {
  return options.filter(option => option.type === 'normal').map(option => option.value)
}

const findResetValue = (options: MultiCheckBoxFormProps['options']) => {
  return options.find(option => option.type === 'reset')?.value
}

export const StyledMultiCheckBoxForm: React.FC<MultiCheckBoxFormProps> = props => {
  const { control, getValues, setValue, formState: { errors } } = useFormContext()
  const [normalValues, setNormalValues] = useState(filterNormalValues(props.options))
  const [resetValue, setResetValue] = useState(findResetValue(props.options))

  useEffect(() => {
    const initValues = getValues(props.name)
    setValue(props.name, initValues)
  })

  useEffect(() => {
    setNormalValues(filterNormalValues(props.options))
    setResetValue(findResetValue(props.options))
  }, [props.options])

  return(
    <div data-cy={props.dataCy}>
      <QuestionnaireFormLabel htmlFor={props.name} label={props.label} required={props.required} annotationNote={props.annotationNote}/>
      { props.messageInMiddle && props.messageInMiddle }
      <Controller
        name={props.name}
        control={control}
        rules={props.rules}
        render={({ field: { value = [], onChange } }) => (
          <StyledNewCheckboxContainer theme={appTheme}>
            {props.options.map((option: NewCheckboxProps) => (
              <NewCheckBox
                key={option.value}
                label={option.label}
                resetLabel={option.resetLabel}
                name={props.name}
                value={option.value}
                checked={value.includes(option.value.toString())}
                onChange={(e) => {
                  if (option.value === 'all') {
                    onChange(e.target.checked ? ['all', ...normalValues] : [])
                  } else if (option.value === resetValue) {
                    onChange(e.target.checked ? [resetValue] : []);
                  } else {
                    const newValue = e.target.checked
                      ? [...value.filter(v => v !== resetValue), option.value]
                      : value.filter(v => v !== option.value);
                    const allSelected = normalValues.every(val =>
                      newValue.includes(val)
                    );

                    onChange(allSelected
                      ? [...newValue, 'all']
                      : newValue.filter(v => v !== 'all')
                    )
                  }
                  props.onChange && props.onChange(e)
                }}
              />
            ))}
          </StyledNewCheckboxContainer>
        )}
      />
      {errors[props.name] && <FormErrorMessage message={String(errors[props.name]?.message)} />}
    </div>
  )
}
